<template>
  <b-card
    no-body
    v-draggable
    ref="google_subcard"
    id="google-subcard"
  >
    <!-- Header -->
    <b-card-header
      style="height: 25px; cursor: move"
      class="pl-2 pb-0 pt-0"
      header-tag="div"
      header-bg-variant="dark"
    >
      <b-row class="mx-0 px-0">
        <b-col class="mx-0 px-0" cols="11">
          <font-awesome-icon class="mx-0" :icon="['fab', 'google']" />oogle
          <span v-if="isCompetitor">
            {{ $t("Webportal.CX.Dashboard.Exploration.Competitor") }}
          </span>
        </b-col>
        <b-col class="mx-0 px-0" cols="1" style="text-align: right">
          <font-awesome-icon
            class="mx-1 clickable"
            :icon="['far', 'times']"
            @click="close()"
          />
        </b-col>
      </b-row>
    </b-card-header>

    <!-- Content -->
    <div v-if="isLoading" :class="{ spinner: isLoading }" style="height:200px"></div>
    <b-card-text 
      v-else-if="GoogleData != null"
      class="pl-3 pr-3" 
      style="overflow: auto"
    >
      <!-- Top menu -->
      <b-row>
        <b-col class="subcard-title mb-3">
          {{ headLine || GoogleData.Name }}
        </b-col>
      </b-row>
      
      <b-button-group>
        <CompetititorDropbox
          v-if="GoogleData.Competitors && GoogleData.Competitors.length > 0"
          :competitorList="GoogleData.Competitors"
          @openCompetitor="openCompetitor"
        />
        <b-button :href="GoogleMapsURI" target="_blank">
          <font-awesome-icon :icon="['far', 'map-marked-alt']" /> {{$t("Webportal.CX.Dashboard.Explorations.Google.MapsLink")}}
        </b-button>
      </b-button-group>

      <template v-if="!isCompetitor">
        <!-- Ratings -->
        <ScoresChart 
          :Scores="GoogleData.OverallScores"
          :Width="600"
          :Caption="$t('Webportal.CX.Dashboard.Explorations.ChartTableCaption')"/>
        <hr>

        <table class="table mb-5 app-ctrl" v-if="GoogleData.Ratings">
          <thead>
            <th class="border-top-0">{{ $t("Webportal.CX.Explorations.RatingsLabel") }}</th>
            <th class="border-top-0" colspan="3">{{ $t("Webportal.CX.Dashboard.Exploration.History") }}</th>
            <th class="border-top-0" colspan="3">{{ $t("Webportal.CX.Dashboard.Exploration.CurrentValue") }}</th>
          </thead>
          <RatingTableRow
            v-for="item in GoogleData.Ratings"
            :key="item.title"
            :Title="item.RatingTypeName"
            :translationPrefix="'Webportal.CX.Dashboard.Exploration.Google.'"
            :Ratings="item.RatingTypeResults" />
        </table>

        <hr>
      </template>
      <template v-else>
        <!-- Ratings -->
        <ScoresChart 
          :Scores="GoogleData.Google.OverallScores"
          :Width="600"
          :Caption="$t('Webportal.CX.Dashboard.Explorations.ChartTableCaption')"/>
        <hr>

        <table class="table mb-5" v-if="GoogleData.Google.Ratings">
          <thead>
            <th class="border-top-0">{{ $t("Webportal.CX.Explorations.RatingsLabel") }}</th>
            <th class="border-top-0" colspan="3">{{ $t("Webportal.CX.Dashboard.Exploration.History") }}</th>
            <th class="border-top-0" colspan="3">{{ $t("Webportal.CX.Dashboard.Exploration.CurrentValue") }}</th>
          </thead>
          <RatingTableRow
            v-for="item in GoogleData.Google.Ratings"
            :key="item.title"
            :Title="item.RatingTypeName"
            :translationPrefix="'Webportal.CX.Dashboard.Exploration.Google.'"
            :Ratings="item.RatingTypeResults" />
        </table>

        <hr>
      </template>

      <!-- Google Reviews -->
      <div v-if="Reviews.length <= 0" class="pb-3">
        {{ $t("Webportal.CX.Dashboard.Exploration.NoReviews") }}
      </div>

      <div v-else>
        <p>
          {{ $t("Webportal.CX.Dashboard.Exploration.Google.Reviews") }}:
        </p>
        <div class="pb-2" style="overflow: auto; height: 50%">
          <Accordion
            v-for="(review, index) in Reviews"
            :key="index"
            class="mb-2"
          >
            <b-row slot="short" class="mb-2" style="width: 100%">
              <b-col class="pr-0" cols="3">
                <b-avatar
                  variant="primary"
                  size="3em"
                  badge-variant="transparent"
                  :src="review.ProfilePhotoUrl"
                >
                  <template v-slot:badge v-if="getLanguageCode(review)">
                    <span :class="'flag-icon flag-icon-' + getLanguageCode(review)"/>
                  </template>
                </b-avatar>
              </b-col>
              <b-col>
                <b-row
                  class="m-0 p-0"
                  style="font-size: 0.9rem; font-weight: 600; line-height: 1"
                >
                  <b-col class="m-1 p-0 ml-n1">
                    <span>{{ review.AuthorName }}</span>
                  </b-col>
                </b-row>
                <b-row class="m-0 p-0 ml-n4 mb-n2" style="line-height: 1">
                  <b-col cols="7">
                    <b-form-rating
                      variant="warning"
                      class="m-0 p-0 app-ctrl"
                      no-border
                      readonly
                      size="sm"
                      :value="review.Rating"
                    />
                  </b-col>
                  <b-col class="m-0 p-0" cols="5" style="display: flex">
                    <span>({{ new Date(review.Time).toLocaleDateString() }})</span>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <template slot="long">
              <!-- Review text message -->
              <p class="mb-2" v-if="review.Text">
                {{ review.Text }}
              </p>
            </template>
          </Accordion>
        </div>
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import Accordion from "../Accordion";
import CompetititorDropbox from "./CompetitorDropbox";
import RatingDistribution from "./RatingDistribution";
import DashboardService from "@/services/DashboardService";
import ScoresChart from "@/components/CX/ExplorationCard/ScoresChart";
import CompetitorRatingTableRow from "@/components/CX/CompetitorRatingTableRow";
import ScoreHistory from "./ScoreHistory";
import RatingTableRow from "@/components/CX/ExplorationCard/RatingTableRow";

export default {
  name: "GooglePopup",
  components: {
    Accordion,
    RatingDistribution,
    CompetititorDropbox,
    ScoreHistory,
    ScoresChart,
    CompetitorRatingTableRow,
    RatingTableRow
  },
  props: {
    googleData: {
      type: Object,
      default: () => {}
    },
    address: String,
    googlePlacesID: String,
    headLine: String,
    isCompetitor: Boolean,
    competitorId: Number
  },
  data() {
    return {
      GoogleData: null,
      isLoading: true
    };
  },
  watch: {},
  computed: {
    ReviewCount: function() {
      return (
        this.GoogleData?.Google?.ReviewCount ||
        this.GoogleData?.Reviews?.length ||
        0
      );
    },

    OverallScore: function() {
      return (
        this.GoogleData?.Threshold?.Value ||
        this.GoogleData?.Google?.RatingsByName?.OverallScore?.Value ||
        0
      );
    },

    Reviews: function() {
      return this.GoogleData?.Reviews || this.GoogleData?.Google?.Reviews || [];
    },

    GoogleMapsURI: function() {
      if(this.googlePlacesID) {
        return 'https://www.google.com/maps/place/?q=place_id:' + this.googlePlacesID;
      }
      let fullAddress = this.GoogleData.FullAddress;
      if (this.isCompetitor) {
        fullAddress = `${this.GoogleData.Name}, ${fullAddress}`;
      } else {
        fullAddress = this.address
      }

      return 'https://www.google.com/maps/search/?api=1&query='+ encodeURIComponent(fullAddress);
    }
  },
  created: function() {
    window.addEventListener('keyup', this.handleKeyUp);
  },
  mounted: function() {
    if (this.isCompetitor) {
      this.getCompetitor(this.competitorId).then(response => {
        this.GoogleData = response.data;
        this.GoogleData.Google.RatingsByName = _.keyBy(
          this.GoogleData.Google.Ratings,
          "Name"
        );
      }).finally(() => {
        this.isLoading = false;
      });
    } else {
      // Clone prop
      this.GoogleData = { ...this.googleData };
      // use setTimeout() to await next event cycle/finished building
      window.setTimeout(() => {this.isLoading = false}, 0);
    }
  },
  methods: {
    openCompetitor(id) {
      this.$emit("open-competitor", id);
    },

    getCompetitor(competitorId) {
      return DashboardService.getCompetitor(competitorId, { google: true });
    },

    getLanguageCode(review) {
      if (typeof review.Language == "undefined") return false;
      switch (review.Language) {
        case "en":
          return "gb";
        case "en-US":
          return "gb";
        case "da":
          return "dk";
        case "sv":
          return "se";
        case "el":
          return "gr";
        case "et":
          return "ee";
        case "he":
          return "il";
        case "uk":
          return "ua";
        default:
          return review.Language;
      }
    },
    close() {
      window.removeEventListener('keyup', this.handleKeyUp);
      this.$emit('close');
    },
    handleKeyUp(event) {
      if (event.key == 'Escape') {
        this.close();
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.subcard-title {
  font-size: 1.5rem;
}
#google-subcard {
  position: fixed !important;
  width: 650px;
}
</style>
